import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import _ from "lodash";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import PandaDocContainer from "components/PandaDocContainer";
import actions from "actions/parent";
import { useGetDocumentById } from "hooks/useGetDocumentById";
import useGetTreatmentPlanUrl from "../hooks/useGetTreatmentPlanUrl";
import useGetPandaDocSessionForDocument from "../../../hooks/api/documents/useGetPandaDocSessionForDocument";
import { getTaskDisplayName } from "../utils/getTaskDisplayName";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface TreatmentPlanSignatureProps {
  open: boolean;
  onClose: () => void;
  treatmentPlan: any;
  onSubmitTreatmentPlanApproval?: () => Promise<void>;
  optimisticUpdateTask: (taskId: number) => Promise<void>;
  data: any;
}

const TreatmentPlanSignature = ({
  open,
  onClose,
  treatmentPlan,
  onSubmitTreatmentPlanApproval = async () => {},
  optimisticUpdateTask,
  data,
}: TreatmentPlanSignatureProps) => {
  const { t } = useTranslation(["dashboard"]);
  const dispatch = useDispatch();
  const [numPages, setNumPages] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [internalOpen, setInternalOpen] = useState(true);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Task created after clinician verification carry `documentId` in
  // metadata. When present, we can resolve the PandaDoc session directly for
  // that document — this works for BOTH the multi-step (generated) track and
  // the legacy manual-upload track, since setTreatmentPlanApprovedByClinical
  // creates a PandaDoc doc in both cases. Only tasks predating this field
  // fall back to the Redux `treatmentPlan` path.
  const metadataDocumentId = data?.metadata?.documentId ?? data?.metadata?.document_id;
  const hasDirectDocumentSession = !!metadataDocumentId;

  // Mirror the BCBA Documents tab rule: only rows with verification in
  // {"UNVERIFIED","AWAITING_SIGNATURE","VERIFIED"} are on the multi-step track.
  // Rows with verification=null fall through to the legacy uploaded-doc flow,
  // where the FE must call PUT /treatment-plans/:planId/approve.
  const {
    data: documentRow,
    isLoading: documentRowLoading,
    error: documentRowError,
  } = useGetDocumentById(metadataDocumentId, {
    enabled: open && hasDirectDocumentSession,
  }) as { data: any; isLoading: boolean; error: unknown };
  const isMultiStepVerification =
    documentRow?.verification === "UNVERIFIED" ||
    documentRow?.verification === "AWAITING_SIGNATURE" ||
    documentRow?.verification === "VERIFIED";
  // If the document lookup failed we cannot tell which verification track the
  // doc is on, so we must not fall back to either branch — the multi-step
  // branch assumes the webhook will close the task, and the legacy branch
  // would incorrectly hit PUT /treatment-plans/:planId/approve on a
  // generated doc. Block submission and surface an error instead.
  const documentRowFailed = hasDirectDocumentSession && !!documentRowError;

  const {
    pandaDocSession: documentPandaDocSession,
    isLoading: documentSessionLoading,
    error: documentSessionError,
  } = useGetPandaDocSessionForDocument(metadataDocumentId, open && hasDirectDocumentSession);

  const pandaDocSessionId = hasDirectDocumentSession
    ? documentPandaDocSession?.pandaDocSessionId
    : treatmentPlan?.panda_doc_session_id;
  const pandaDocDocumentId = hasDirectDocumentSession
    ? documentPandaDocSession?.pandaDocDocumentId
    : treatmentPlan?.panda_doc_document_id;
  const isPandaDoc = !!pandaDocSessionId && !!pandaDocDocumentId;

  const {
    treatmentPlanUrl,
    isLoading: urlLoading,
    error: urlError,
  } = useGetTreatmentPlanUrl(treatmentPlan, open && !hasDirectDocumentSession && !isPandaDoc);

  const isLegacyPDF = !hasDirectDocumentSession && treatmentPlanUrl && !isPandaDoc;
  const taskTitle = getTaskDisplayName(data, t);
  const isUrlError = hasDirectDocumentSession
    ? documentRowFailed || !!documentSessionError || (!documentSessionLoading && !isPandaDoc)
    : urlError?.message || !treatmentPlanUrl;
  const isLoading = hasDirectDocumentSession
    ? documentRowLoading || (documentSessionLoading && !isPandaDoc)
    : urlLoading || !treatmentPlan || (isLegacyPDF && !numPages);
  const errorMessage = hasDirectDocumentSession
    ? documentRowFailed
      ? t("dashboard:parent.myTasks.treatmentPlanSignature.documentDetailsError")
      : t("dashboard:parent.myTasks.treatmentPlanSignature.sessionNotAvailable")
    : urlError?.message || t("dashboard:parent.myTasks.treatmentPlanSignature.loadError");
  const shouldShowError = open && isUrlError && !isPandaDoc;
  const shouldShowLoading = open && isLoading;

  useEffect(() => {
    if (!open) return;
    // Legacy (non-multi-step) TPs still need the FE to call
    // approveTreatmentPlan(planId), and that plan id lives on the Redux
    // `treatmentPlan` populated by getActiveTreatmentPlan(). We wait for the
    // document row so we know whether we're on the multi-step track before
    // firing a redundant fetch.
    if (documentRowLoading) return;
    if (isMultiStepVerification) return;
    if (treatmentPlan?.id) return;
    dispatch(actions.getActiveTreatmentPlan());
  }, [open, documentRowLoading, isMultiStepVerification, treatmentPlan?.id, dispatch]);

  const handleClose = () => {
    setInternalOpen(false);
  };

  const handleOpenInNewTab = () => {
    if (treatmentPlanUrl) {
      window.open(treatmentPlanUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleComplete = async () => {
    try {
      // If we couldn't load the document row we don't know which verification
      // track the doc is on — refuse to submit rather than guess and
      // potentially call approve on a multi-step (generated) doc.
      if (documentRowFailed) {
        throw new Error("Unable to determine treatment plan verification flow.");
      }
      // Multi-step (generated) docs are closed server-side by the PandaDoc
      // webhook -> advanceVerificationFromWebhook (flips verification to
      // VERIFIED, replaces the S3 file, marks the caregiver task complete).
      // Legacy uploaded TPs still need the FE to hit
      // PUT /treatment-plans/:planId/approve to flip approved=true and close
      // the TREATMENT_PLAN_SIGNATURE task.
      if (!isMultiStepVerification) {
        if (!treatmentPlan?.id) {
          throw new Error("Treatment plan is still loading.");
        }
        await onSubmitTreatmentPlanApproval();
      }
      await optimisticUpdateTask(data.id);
    } catch (error) {
      console.error("Error completing treatment plan task:", error);
      return;
    }

    handleClose();
  };

  const handlePandaDocComplete = useCallback(() => {
    setCompleted(true);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
  };

  if (!shouldShowLoading && !isPandaDoc && !isLegacyPDF) {
    return (
      <ResponsiveModal
        open={internalOpen}
        onClose={handleClose}
        onTransitionExited={onClose}
        onOpen={() => {
          setInternalOpen(true);
        }}
        title={t("dashboard:parent.myTasks.treatmentPlanSignature.notAvailableTitle")}
        maxWidth="sm"
      >
        <Box sx={{ py: 2 }}>
          <Alert severity="error">
            {t("dashboard:parent.myTasks.treatmentPlanSignature.notAvailableMessage")}
          </Alert>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose} variant="contained">
              {t("dashboard:parent.myTasks.common.closeButton")}
            </Button>
          </Box>
        </Box>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      open={internalOpen}
      onClose={handleClose}
      onTransitionExited={onClose}
      onOpen={() => {
        setInternalOpen(true);
      }}
      title={taskTitle}
      maxWidth="lg"
      dialogSx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          maxHeight: "90vh",
          width: "90vw",
          maxWidth: "90vw",
        },
      }}
      drawerSx={{
        height: "95vh",
        maxHeight: "95vh",
      }}
      drawerContentSx={{
        height: "calc(95vh - 140px)",
        maxHeight: "calc(95vh - 140px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: isDesktop ? "80vh" : "100%",
          minHeight: isDesktop ? "600px" : "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            {taskTitle}
          </Typography>
          {treatmentPlanUrl && (
            <IconButton
              onClick={handleOpenInNewTab}
              size="small"
              title={t("dashboard:parent.myTasks.treatmentPlanSignature.openInNewTab")}
              sx={{
                color: "primary.main",
                "&:hover": { backgroundColor: "primary.light", color: "white" },
              }}
            >
              <OpenInNewIcon />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            flexGrow: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            position: "relative",
            minHeight: isDesktop ? "none" : "300px",
            overflow: isDesktop ? "hidden" : "auto",
          }}
        >
          {shouldShowLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "background.default",
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                {t("dashboard:parent.myTasks.treatmentPlanSignature.loadingDocument")}
              </Typography>
            </Box>
          )}

          {shouldShowError && (
            <Box sx={{ p: 3 }}>
              <Alert
                severity="error"
                action={
                  <Button size="small" onClick={() => window.location.reload()}>
                    {t("dashboard:parent.myTasks.treatmentPlanSignature.retryButton")}
                  </Button>
                }
              >
                {errorMessage}
              </Alert>
            </Box>
          )}

          {isPandaDoc && (
            <Box sx={{ height: "100%", minHeight: "60vh", padding: 2 }}>
              <PandaDocContainer
                pandaDocSessionId={pandaDocSessionId}
                onDocumentCompleted={handlePandaDocComplete}
                pandaDocId={pandaDocDocumentId}
              />
            </Box>
          )}

          {isLegacyPDF && (
            <Box
              sx={{
                height: "100%",
                minHeight: "60vh",
                overflow: isDesktop ? "auto" : "visible",
                padding: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ...(isDesktop && {
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: "4px",
                  },
                }),
              }}
            >
              <Document
                file={treatmentPlanUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading=""
              >
                {numPages &&
                  _.range(numPages).map((page) => {
                    return (
                      <Page
                        key={page}
                        width={Math.min(window.innerWidth * 0.8, 800)}
                        pageNumber={page + 1}
                        style={{ marginBottom: "10px" }}
                      />
                    );
                  })}
              </Document>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="medium" sx={{ minWidth: "120px" }}>
            {t("dashboard:parent.myTasks.common.closeButton")}
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="primary"
            size="medium"
            sx={{ minWidth: "120px" }}
            disabled={isPandaDoc && !completed}
          >
            {t("dashboard:parent.myTasks.treatmentPlanSignature.approveButton")}
          </Button>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default TreatmentPlanSignature;
